// src/services/api/dayCloseReflectionFeedback.ts
import { getFunctions, httpsCallable, HttpsCallableOptions } from "firebase/functions";
import { logDev, logInfo } from '../../utils/logger';

// Day Close 反思回饋請求參數型別
export interface DayCloseReflectionFeedbackRequest {
  reflectionContent: string;
  dayContext: {
    completed: string[];              // 已完成的 TimeBox IDs
    incomplete: {                     // 未完成及處理方式
      timeBoxId: string;
      action: 'add_todo' | 'discard';
    }[];
    timeBoxes: {                      // 當日行程精簡資料
      id: string;
      title: string;
      category: string;
      startTime: string;
      endTime: string;
      status: string;
    }[];
    captures?: {                      // 當日相關記錄
      timeBoxId?: string;
      content: string;
    }[];
  };
  date: string;
}

// Day Close 反思回饋回應型別
export interface DayCloseReflectionFeedbackResponse {
  emotionalSupport: string;           // 情緒支持訊息
  learningCapture?: string;           // 條件性學習捕捉內容
  inspirationalContent?: {            // 啟發性內容
    text: string;                     // 敘述性段落
  };
  _meta?: {
    analysisTime?: number;            // 分析耗時（毫秒）
    contentLength?: number;           // 反思內容長度
    hasLearningContent?: boolean;     // 是否包含學習洞察
    aiProvider?: string;              // AI 服務提供者
    processingTime?: number;          // 處理時間
    businessContext?: {
      taskType?: string;
      complexity?: string;
      urgency?: string;
    };
    reflectionData?: {
      date?: string;
      reflectionLength?: number;
      timeBoxesCount?: number;
    };
  };
}

/**
 * 生成 Day Close 反思智能回饋
 * 基於用戶的反思內容和當日行程資料，生成溫暖的個人化回饋
 * 
 * @param request - 反思回饋請求參數
 * @param signal - 可選的 AbortSignal 用於取消請求
 * @returns 包含情緒支持、學習捕捉和溫暖結語的回饋內容
 */
export const generateDayCloseReflectionFeedback = async (
  request: DayCloseReflectionFeedbackRequest,
  signal?: AbortSignal
): Promise<DayCloseReflectionFeedbackResponse> => {
  try {
    const functions = getFunctions();
    
    // 設置可取消的選項
    const options: HttpsCallableOptions = {};
    if (signal && signal.aborted) {
      // 如果 signal 已經被中止，直接拋出錯誤
      throw new DOMException('Request aborted', 'AbortError');
    }
    
    const generateFeedbackFunc = httpsCallable(
      functions, 
      'generateDayCloseReflectionFeedback',
      options
    );
    
    const params = {
      reflectionContent: request.reflectionContent.trim(),
      dayContext: request.dayContext,
      date: request.date
    };
    
    logDev('DayCloseReflection', '正在生成反思回饋', {
      date: params.date,
      contentLength: params.reflectionContent.length,
      timeBoxCount: params.dayContext.timeBoxes.length
    });
    
    const result = await generateFeedbackFunc(params);
    
    const responseData = result.data as DayCloseReflectionFeedbackResponse;
    
    logInfo('DayCloseReflection', '反思回饋生成完成', {
      hasEmotionalSupport: !!responseData.emotionalSupport,
      hasLearningCapture: !!responseData.learningCapture,
      hasInspirationalContent: !!responseData.inspirationalContent,
      analysisTime: responseData._meta?.analysisTime
    });
    
    return responseData;
    
  } catch (error) {
    // 檢查是否是取消錯誤
    if (signal?.aborted) {
      logDev('DayCloseReflection', '請求已取消');
      throw new DOMException('Request aborted', 'AbortError');
    }
    
    console.error('生成 Day Close 反思回饋失敗:', error);
    
    // 重新拋出錯誤，讓上層處理
    throw error;
  }
};

/**
 * 生成 Day Close 反思回饋的簡化版本（不需要 signal）
 * 適用於大多數不需要取消功能的場景
 */
export const generateDayCloseReflectionFeedbackSimple = async (
  reflectionContent: string,
  dayContext: DayCloseReflectionFeedbackRequest['dayContext'],
  date: string
): Promise<DayCloseReflectionFeedbackResponse> => {
  return generateDayCloseReflectionFeedback({ reflectionContent, dayContext, date });
};

/**
 * 生成降級回饋
 * 當 AI 分析失敗時，提供通用但溫暖的回饋內容
 */
export const generateFallbackFeedback = (
  reflectionContent: string
): DayCloseReflectionFeedbackResponse => {
  const hasContent = reflectionContent.trim().length > 0;
  
  return {
    emotionalSupport: hasContent 
      ? "謝謝你花時間反思今天的經歷。每一天的感受和觀察都很珍貴，今天的你已經很棒了。"
      : "今天辛苦了！不管發生了什麼，你都已經盡力了。",
    _meta: {
      analysisTime: 0,
      contentLength: reflectionContent.length,
      hasLearningContent: false
    }
  };
};

/**
 * 計算反思內容的 hash，用於避免重複分析
 */
export const calculateReflectionContentHash = (content: string): string => {
  // 簡單的 hash 函數，用於去重
  let hash = 0;
  const cleanContent = content.trim().toLowerCase();
  
  if (cleanContent.length === 0) return '0';
  
  for (let i = 0; i < cleanContent.length; i++) {
    const char = cleanContent.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // 轉換為 32 位整數
  }
  
  return Math.abs(hash).toString(36);
};